import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  useLazyListConversationsQuery,
  useLazyGetConversationQuery,
  syncHookFormAsQuery,
} from '@/store/api/admin/messagesApi';
import { useAdminUser } from './useAdminUser';
import { 
  IConversation, 
  FilterType, 
  SortType, 
  FILTER_VALUES,
  SORT_VALUES,
} from '@/types/admin/messages';
import { useOptimizedPolling } from './useAppState';
import { PERFORMANCE_CONSTANTS } from '@/constants/admin/performance';
import { useDataTableState } from '@/contexts/admin/DataTableContext';
import { 
  formatMessageTime as formatMessageTimeUtil, 
  formatMessageDate as formatMessageDateUtil,
} from '@/utils/common/formatting';

// Hook for managing conversations list with pagination
export const useConversations = () => {
  const [filterType, setFilterType] = useState<FilterType>('open');
  const [sortType, setSortType] = useState<SortType>('latest');
  const table = useDataTableState('admin:messages');
  const conversationQuery = table.searchQuery || '';
  const page = table.paginationModel?.page || 0;
  const pageSize = table.paginationModel?.pageSize || PERFORMANCE_CONSTANTS.CONVERSATIONS_PER_PAGE;
  const [allConversations, setAllConversations] = useState<IConversation[]>([]);
  const [hasNextPage, setHasNextPage] = useState(true);
  
  const [getConversations, { data, isLoading, error }] = useLazyListConversationsQuery();
  
  // Get property ID from admin user hook (correctly reads from adminUI slice)
  const adminUser = useAdminUser();
  const propertyId = adminUser.selectedPropertyId;
  
  // Use refs to store current values to avoid dependency issues
  const tableRef = useRef(table);
  const pageSizeRef = useRef(pageSize);
  
  // Update refs when values change
  useEffect(() => {
    tableRef.current = table;
  }, [table]);
  
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Reset pagination when filters change
  const resetPagination = useCallback((clearConversations: boolean = true) => {
    tableRef.current.setPagination({ page: 0, pageSize: pageSizeRef.current });
    if (clearConversations) {
      setAllConversations([]);
    }
    setHasNextPage(true);
  }, []); // No dependencies to prevent recreation
  
  // Fetch conversations when filters change or page changes
  useEffect(() => {
    if (propertyId) {
      const searchParams = syncHookFormAsQuery({
        propertyId: propertyId.toString(),
        pageNum: page.toString(),
        pageSize: pageSize.toString(),
        isAdmin: 'true',
        filterType,
        sortType,
        searchQuery: conversationQuery || undefined,
      });
      getConversations(searchParams.toString());
    }
  }, [propertyId, sortType, filterType, conversationQuery, page, pageSize]); // Remove getConversations from dependencies

  // Update conversations when data changes
  useEffect(() => {
    if (data?.response) {
      const newConversations = data.response;
      
      if (page === 0) {
        // First page - replace all conversations (this handles filter changes)
        setAllConversations(newConversations);
      } else {
        // Subsequent pages - append to existing conversations, avoiding duplicates
        setAllConversations(prev => {
          const existingIds = new Set(prev.map(conv => conv.id));
          const uniqueNewConversations = newConversations.filter(conv => !existingIds.has(conv.id));
          return [...prev, ...uniqueNewConversations];
        });
      }
      
      // Check if we have more pages
      setHasNextPage(newConversations.length === pageSize);
    }
  }, [data, page, pageSize]);

  // Reset when filters change - don't clear conversations immediately to avoid flickering
  useEffect(() => {
    resetPagination(false);
  }, [filterType, sortType, conversationQuery]); // Remove resetPagination from dependencies

  // Reset and clear conversations when propertyId changes (e.g., user switches property)
  useEffect(() => {
    resetPagination(true); // Clear conversations when property changes
  }, [propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(() => {
    if (!isLoading && hasNextPage) {
      table.setPagination({ page: page + 1, pageSize });
    }
  }, [isLoading, hasNextPage, table, page, pageSize]);
  
  return {
    conversations: allConversations,
    isLoading,
    error,
    filterType,
    setFilterType,
    sortType,
    setSortType,
    conversationQuery,
    setConversationQuery: (q: string) => table.setSearch(q),
    hasNextPage,
    loadMore,
    refetch: () => {
      resetPagination(true); // Clear conversations on manual refetch
      if (propertyId) {
        const searchParams = syncHookFormAsQuery({
          propertyId: propertyId.toString(),
          pageNum: '0',
          pageSize: pageSize.toString(),
          isAdmin: 'true',
          filterType,
          sortType,
          searchQuery: conversationQuery || undefined,
        });
        getConversations(searchParams.toString());
      }
    },
  };
};

// Hook for managing single conversation
export const useConversation = (conversationId?: number) => {
  const [getConversation, { data, isLoading, error }] = useLazyGetConversationQuery();
  
  // Use optimized polling that respects app state
  const fetchConversation = useCallback(() => {
    if (conversationId) {
      getConversation({ conversationId });
    }
  }, [conversationId, getConversation]);
  
  // Ensure immediate fetch when conversationId changes
  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);
  
  // Optimized polling with app state awareness
  useOptimizedPolling(
    fetchConversation,
    10000, // 10 second interval
    !!conversationId // Only poll if we have a conversation ID
  );
  
  const conversation = data?.response;
  
  return {
    conversation,
    isLoading,
    error,
    refetch: fetchConversation,
  };
};

// Hook for conversation utilities
export const useConversationUtils = () => {
  // Truncate subject text (matching admin-web logic)
  const truncateSubject = useCallback((subject: string, maxLength = 30) => {
    if (subject.length > maxLength) {
      return `${subject.slice(0, maxLength - 3)}...`;
    }
    return subject;
  }, []);
  
  // Truncate message content (matching admin-web logic)
  const truncateMessage = useCallback((content: string, maxLength = 30) => {
    if (!content || content.length === 0) {
      return 'Send the first message...';
    }
    if (content.length > maxLength) {
      return `${content.slice(0, maxLength - 3)}...`;
    }
    return content;
  }, []);
  
  // Check if conversation is stale (matching admin-web logic)
  const isConversationStale = useCallback((conversation: IConversation) => {
    const lastInteractionDate = conversation.messages?.length > 0
      ? new Date(conversation.messages[0].createdAt)
      : new Date(conversation.createdAt);
    
    const now = new Date();
    const daysDiff = (now.getTime() - lastInteractionDate.getTime()) / (1000 * 60 * 60 * 24);
    
    return daysDiff > 30;
  }, []);
  
  // Get recipient display name (matching admin-web logic)
  const getRecipientDisplayName = useCallback((conversation: IConversation) => {
    const conversationAccounts = (conversation.accounts || []).filter(
      (acct) => acct.ConversationAccount?.type === 'resident'
    );
    
    if (conversationAccounts.length === 0) {
      return 'No recipients';
    }
    
    if (conversationAccounts.length === 1) {
      const account = conversationAccounts[0];
      return account.firstName && account.lastName
        ? `${account.firstName} ${account.lastName}`
        : account.email;
    }
    
    return `${conversationAccounts.length} Recipients`;
  }, []);
  
  // Format message timestamp - use canonical formatter
  const formatMessageTime = useCallback((createdAt: string) => {
    return formatMessageTimeUtil(createdAt);
  }, []);
  
  // Format message date - use canonical formatter
  const formatMessageDate = useCallback((createdAt: string) => {
    return formatMessageDateUtil(createdAt);
  }, []);
  
  // Truncate file name (matching admin-web logic)
  const truncateFileName = useCallback((name: string, maxLength = 17) => {
    if (name.length > maxLength) {
      return `${name.substring(0, maxLength)}...`;
    }
    return name;
  }, []);
  
  // Get file extension
  const getFileExtension = useCallback((fileName: string) => {
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1);
    return extension;
  }, []);
  
  return {
    truncateSubject,
    truncateMessage,
    isConversationStale,
    getRecipientDisplayName,
    formatMessageTime,
    formatMessageDate,
    truncateFileName,
    getFileExtension,
  };
};

// Export filter and sort options for UI components
export const useMessageFilters = () => {
  return {
    filterOptions: Object.values(FILTER_VALUES),
    sortOptions: Object.entries(SORT_VALUES).map(([key, value]) => ({
      key: key as SortType,
      value,
    })),
  };
};
